import React, { useEffect, useState } from "react";
import axios from "axios";
import { Base_URL } from "../url";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Button } from "bootstrap/dist/js/bootstrap.min";

export default function VendorInvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate=useNavigate();
  // Track expanded rows and their items
  const [expanded, setExpanded] = useState({});
  const [itemsLoading, setItemsLoading] = useState({});
  const [posting, setPosting] = useState({}); // Track posting state per invoice
  const [gstData, setGstData] = useState({}); // Store GST rates for items

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const resp = await axios.get(`${Base_URL}/VendorInvoice`);
        if (!mounted) return;
        setInvoices(Array.isArray(resp.data) ? resp.data : []);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Failed to load vendor invoices");
        toast.error("Failed to load vendor invoices");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString();
    } catch (e) {
      return String(iso).split("T")[0];
    }
  };

  // Fetch GST data for a specific item
  const fetchGstDataForItem = async (itemCode) => {
    if (!itemCode) {
      return { CGST: 0, IGST: 0, SGST: 0 };
    }

    try {
      console.log("Fetching GST data for item:", itemCode);
      
      const response = await axios.get(`${Base_URL}/VendorInvoice/GetGst?itemCode=${encodeURIComponent(itemCode)}`);
      console.log("GST API Response for item", itemCode, ":", response.data);

      if (response.data) {
        const data = response.data;
        
        // Handle different response structures
        let gstRates = { 
          CGST: 0, 
          IGST: 0, 
          SGST: 0 
        };

        // Extract GST rates from the response
        if (data.CGST_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.CGST_Percent) || 0,
            IGST: parseFloat(data.IGST_Percent) || 0,
            SGST: parseFloat(data.SGST_Percent) || 0
          };
        }
        // If response is an array, take the first item
        else if (Array.isArray(data) && data.length > 0) {
          const firstItem = data[0];
          gstRates = {
            CGST: parseFloat(firstItem.CGST_Percent) || 0,
            IGST: parseFloat(firstItem.IGST_Percent) || 0,
            SGST: parseFloat(firstItem.SGST_Percent) || 0
          };
        }
        // Handle different property names
        else if (data.cgsT_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.cgsT_Percent) || 0,
            IGST: parseFloat(data.igsT_Percent) || 0,
            SGST: parseFloat(data.sgsT_Percent) || 0
          };
        }

        console.log("Final GST Rates for item", itemCode, ":", gstRates);
        return gstRates;
      }
      
      return { CGST: 0, IGST: 0, SGST: 0 };
      
    } catch (error) {
      console.error("Error fetching GST data for item", itemCode, ":", error);
      return { CGST: 0, IGST: 0, SGST: 0 };
    }
  };

  // Calculate GST amounts based on basic value and GST rates
  const calculateGstAmounts = (basicValue, cgstRate, igstRate, sgstRate) => {
    if (!basicValue) {
      return {
        cgstAmount: 0,
        igstAmount: 0,
        sgstAmount: 0,
        totalAmount: 0
      };
    }

    const amount = parseFloat(basicValue);
    const cgstAmount = (amount * (cgstRate || 0)) / 100;
    const igstAmount = (amount * (igstRate || 0)) / 100;
    const sgstAmount = (amount * (sgstRate || 0)) / 100;
    const totalAmount = amount + cgstAmount + igstAmount + sgstAmount;

    return {
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      igstAmount: parseFloat(igstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2))
    };
  };

  // Fetch vendor invoice items with GST calculation
  const fetchInvoiceItems = async (vendorInvoiceNumber) => {
    try {
      setItemsLoading((prev) => ({ ...prev, [vendorInvoiceNumber]: true }));
      const url = `${Base_URL}/VendorInvoice/VendorInvoiceItems/id?id=${encodeURIComponent(
        vendorInvoiceNumber
      )}`;
      const resp = await axios.get(url);
      const data = Array.isArray(resp.data) ? resp.data : [];

      // Fetch GST data for each item and calculate amounts
      const itemsWithGst = await Promise.all(
        data.map(async (item) => {
          if (item.itemCode) {
            const gstRates = await fetchGstDataForItem(item.itemCode);
            const gstAmounts = calculateGstAmounts(
              item.basicValue || 0,
              gstRates.CGST,
              gstRates.IGST,
              gstRates.SGST
            );

            return {
              ...item,
              cgstRate: gstRates.CGST,
              igstRate: gstRates.IGST,
              sgstRate: gstRates.SGST,
              calculatedCgstValue: gstAmounts.cgstAmount,
              calculatedIgstValue: gstAmounts.igstAmount,
              calculatedSgstValue: gstAmounts.sgstAmount,
              calculatedTotalValue: gstAmounts.totalAmount
            };
          }
          return item;
        })
      );

      setExpanded((prev) => ({
        ...prev,
        [vendorInvoiceNumber]: itemsWithGst,
      }));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load vendor invoice items");
    } finally {
      setItemsLoading((prev) => ({ ...prev, [vendorInvoiceNumber]: false }));
    }
  };

  // Toggle expand/collapse for each row
  const handleRowClick = (vendorInvoiceNumber) => {
    if (expanded[vendorInvoiceNumber]) {
      setExpanded((prev) => {
        const copy = { ...prev };
        delete copy[vendorInvoiceNumber];
        return copy;
      });
    } else {
      fetchInvoiceItems(vendorInvoiceNumber);
    }
  };

  // Handle POST action
  const handlePost = async (vendorInvoiceNumber) => {
    try {
      setPosting((prev) => ({ ...prev, [vendorInvoiceNumber]: true }));
      const url = `${Base_URL}/VendorInvoice/id?id=${encodeURIComponent(vendorInvoiceNumber)}`;
      const resp = await axios.put(url);

      toast.success(`Invoice ${vendorInvoiceNumber} posted successfully!`);

      // Update local state to reflect posted status
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.vendorInvoiceNumber === vendorInvoiceNumber
            ? { ...inv, postedStatus: "Yes" }
            : inv
        )
      );
    } catch (err) {
      console.error(err);
      toast.error(
        `Failed to post invoice ${vendorInvoiceNumber}: ${
          err.response?.data || err.message
        }`
      );
    } finally {
      setPosting((prev) => ({ ...prev, [vendorInvoiceNumber]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">Vendor Invoices</h1>
          <div className="flex"> 
            <ul className="flex " style={{listStyle:"none"}}>
              <li style={{marginRight:"20px", fontSize:"15px", color:"green", cursor:"pointer"}} onClick={()=>navigate('/vendorInvoices')}>Create Vendor Invoice With PO +</li>
              <li style={{marginRight:"20px", fontSize:"15px", color:"green",cursor:"pointer"}} onClick={()=>navigate('/vendorInvoiceWithoutPO')}>Create Vendor Invoice Without PO +</li>
            </ul>
          </div>
          {/* <button
            className="px-3 py-1 bg-white border rounded shadow-sm"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button> */}
        </div>

        <div className="bg-white shadow rounded overflow-x-auto">
          <table className="w-full table-auto text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-4 py-3">Vendor Invoice</th>
                <th className="px-4 py-3">Purchase Order</th>
                <th className="px-4 py-3">Company Code</th>
                <th className="px-4 py-3">Invoice Type</th>
                <th className="px-4 py-3">Posted</th>
                <th className="px-4 py-3">Vendor Invoice Date</th>
               
                
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center">
                    Loading invoices...
                  </td>
                </tr>
              )}

              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center">
                    No invoices found
                  </td>
                </tr>
              )}

              {!loading &&
                invoices.map((inv, idx) => {
                  const isExpanded = expanded[inv.vendorInvoiceNumber];
                  const isPosted =
                    String(inv.postedStatus)?.toLowerCase() === "yes";
                  return (
                    <React.Fragment key={idx}>
                      <tr
                        className="border-t hover:bg-gray-50 transition cursor-pointer"
                        onClick={() =>
                          handleRowClick(inv.vendorInvoiceNumber)
                        }
                      >
                        <td className="px-4 py-3 font-medium text-blue-700">
                          {inv.vendorInvoiceNumber ?? "-"}
                        </td>
                        <td className="px-4 py-3">
                          {inv.purchaseOrderNumber ?? "-"}
                        </td>
                        <td className="px-4 py-3">
                          {String(inv.companyCode ?? "-")}
                        </td>
                        <td className="px-4 py-3">{inv.invoiceType ?? "-"}</td>
                        <td className="px-4 py-3">
                          {isPosted ? (
                            <span className="text-green-700 font-medium">Yes</span>
                          ) : (
                            <span className="text-red-600 font-medium">No</span>
                          )}
                        </td>
                        <td className="px-4 py-3">{formatDate(inv.createdOn)}</td>
                  
                        <td
                          className="px-4 py-3 text-center"
                          onClick={(e) => e.stopPropagation()} // Prevent row toggle
                        >
                          <button
  onClick={() => handlePost(inv.vendorInvoiceNumber)}
  disabled={isPosted || posting[inv.vendorInvoiceNumber]}
  className={`px-3 py-1 rounded text-sm font-medium border ${
    isPosted
      ? "bg-gray-300 text-gray-600 border-gray-400 cursor-not-allowed"
      : posting[inv.vendorInvoiceNumber]
      ? "bg-blue-500 text-white border-blue-600 cursor-not-allowed"
      : "bg-green-600 text-white border-green-700 hover:bg-green-700"
  } transition-colors duration-200`}
>
  {posting[inv.vendorInvoiceNumber]
    ? "Posting..."
    : isPosted
    ? "Posted"
    : "Post"}
</button>
<button onClick={()=>navigate(`/editVendorInvoice/${inv.vendorInvoiceNumber}`)} className="m-2" >
  Edit Vendor Invoice
</button>
                        </td>
                      </tr>

                      {/* Dropdown Row for Items */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} className="bg-gray-50 border-t">
                            {itemsLoading[inv.vendorInvoiceNumber] ? (
                              <div className="p-4 text-center">
                                Loading items...
                              </div>
                            ) : (
                              <div className="p-4">
                                <h4 className="font-semibold mb-3">
                                  Invoice Items with GST Calculation
                                </h4>
                                <table className="min-w-full text-xs border bg-white">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-2 py-2 border">Item Code</th>
                                      <th className="px-2 py-2 border">Description</th>
                                      <th className="px-2 py-2 border">Orig. Qty</th>
                                      <th className="px-2 py-2 border">Recv. Qty</th>
                                      <th className="px-2 py-2 border">Inv. Qty</th>
                                      <th className="px-2 py-2 border">Balance</th>
                                      <th className="px-2 py-2 border">Unit</th>
                                      <th className="px-2 py-2 border">Price</th>
                                      <th className="px-2 py-2 border">Basic Value</th>
                                      <th className="px-2 py-2 border">CGST %</th>
                                      <th className="px-2 py-2 border">CGST Value</th>
                                      <th className="px-2 py-2 border">SGST %</th>
                                      <th className="px-2 py-2 border">SGST Value</th>
                                      <th className="px-2 py-2 border">IGST %</th>
                                      <th className="px-2 py-2 border">IGST Value</th>
                                      <th className="px-2 py-2 border">Total Value</th>
                                      <th className="px-2 py-2 border">Receipt Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expanded[inv.vendorInvoiceNumber]?.map(
                                      (item, itemIndex) => (
                                        <tr key={itemIndex} className="hover:bg-gray-50">
                                          <td className="px-2 py-2 border font-medium">
                                            {item.itemCode}
                                          </td>
                                          <td className="px-2 py-2 border">
                                            {item.description}
                                          </td>
                                          <td className="px-2 py-2 border text-right">
                                            {item.originalQuantity}
                                          </td>
                                          <td className="px-2 py-2 border text-right">
                                            {item.quantityReceived}
                                          </td>
                                          <td className="px-2 py-2 border text-right">
                                            {item.invoiceQuantity}
                                          </td>
                                          <td className="px-2 py-2 border text-right">
                                            {item.balanceQuantity}
                                          </td>
                                          <td className="px-2 py-2 border">{item.unit}</td>
                                          <td className="px-2 py-2 border text-right">
                                            {item.price?.toFixed(2)}
                                          </td>
                                          <td className="px-2 py-2 border text-right font-medium">
                                            {item.basicValue?.toFixed(2)}
                                          </td>
                                          {/* GST Rates */}
                                          <td className="px-2 py-2 border text-right bg-blue-50">
                                            {item.cgst?.toFixed(2)}%
                                          </td>
                                          <td className="px-2 py-2 border text-right bg-blue-50">
                                            {item.cgstValue?.toFixed(2)}
                                          </td>
                                          <td className="px-2 py-2 border text-right bg-green-50">
                                            {item.sgst?.toFixed(2)}%
                                          </td>
                                          <td className="px-2 py-2 border text-right bg-green-50">
                                            {item.sgstValue?.toFixed(2)}
                                          </td>
                                          <td className="px-2 py-2 border text-right bg-yellow-50">
                                            {item.igst?.toFixed(2)}%
                                          </td>
                                          <td className="px-2 py-2 border text-right bg-yellow-50">
                                            {item.igstValue?.toFixed(2)}
                                          </td>
                                          <td className="px-2 py-2 border text-right font-bold bg-gray-100">
                                            {(
                                              (Number(item.basicValue) || 0) +
                                              (Number(item.sgstValue ?? item.calculatedSgstValue ?? item.sgst ?? 0) || 0) +
                                              (Number(item.cgstValue ?? item.calculatedCgstValue ?? item.cgst ?? 0) || 0) +
                                              (Number(item.igstValue ?? item.calculatedIgstValue ?? item.igst ?? 0) || 0)
                                            ).toFixed(2)}
                                          </td>
                                          <td className="px-2 py-2 border">
                                            {formatDate(item.receiptDate)}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>

        {error && <div className="mt-4 text-red-600">{error}</div>}
      </div>
    </div>
  );
}